import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { S as StringService } from './index2.js';
import { T as TrackingService } from './index3.js';
import { T as TranslationService } from './index6.js';
import { b as breakpoints } from './index5.js';
import { a as ButtonAppearance, d as defineCustomElement$5 } from './roche-button2.js';
import { R as Ratio } from './roche-image.types.js';
import { d as defineCustomElement$4 } from './roche-icon2.js';
import { d as defineCustomElement$2, a as defineCustomElement$3 } from './roche-image2.js';

var Layout;
(function (Layout) {
    Layout["DEFAULT"] = "teaser";
    Layout["DEFAULT_WITHOUT_OVERLINE"] = "teaser-without-overline";
})(Layout || (Layout = {}));
const LayoutKeys = Object.keys(Layout);
LayoutKeys
    .map((layout) => Layout[layout]);

const rocheHeroTeaserCss = ":host{display:block;margin-top:3rem;margin-top:0;display:block}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 64rem){:host{margin-top:0}}:host .teaser{display:flex;flex-direction:column;align-items:center}@media (min-width: 48rem){:host .teaser{flex-direction:row;justify-content:space-between}}:host .teaser__text-wrapper{order:1;margin-top:1rem;width:100%}@media (min-width: 48rem){:host .teaser__text-wrapper{order:0;width:calc(50% - calc(24px / 2))}}@media (min-width: 90rem){:host .teaser__text-wrapper{width:41.6666666667%}}:host .teaser__overline{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:400;font-size:1.125rem;line-height:1.6875rem;color:#706b69;margin-bottom:0.25rem}:host .teaser__overline strong{font-weight:600}@media (min-width: 48rem){:host .teaser__overline{font-size:1.125rem;line-height:1.8125rem}}@media (min-width: 90rem){:host .teaser__overline{font-size:1.25rem;line-height:1.875rem}}:host .teaser__title{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:1.875rem;line-height:2.25rem;margin-bottom:1rem}:host .teaser__title strong{font-weight:600}@media (min-width: 48rem){:host .teaser__title{font-size:2.5rem;line-height:3rem}}@media (min-width: 90rem){:host .teaser__title{font-size:3rem;line-height:3.625rem}}:host .teaser__description{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem}@media (min-width: 64rem){:host .teaser__description{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}:host .teaser__buttons-wrapper{margin-top:1.5rem}@media (min-width: 48rem){:host .teaser__buttons-wrapper{margin-top:2rem}}@media (min-width: 90rem){:host .teaser__buttons-wrapper{margin-top:4rem}}:host .teaser__image-wrapper{width:100%}@media (min-width: 48rem){:host .teaser__image-wrapper{width:calc(50% - calc(24px / 2))}}:host .teaser--image-on-left .teaser__text-wrapper{order:1}:host .teaser roche-image{margin-top:0 !important}:host .teaser roche-button{margin-top:0 !important}:host ::slotted(roche-image){margin-top:0 !important}:host ::slotted([slot=secondary-action]){margin-top:0.5rem !important}@media (min-width: 48rem){:host ::slotted([slot=secondary-action]){margin-top:0 !important;margin-left:1rem}}";
const RocheHeroTeaserStyle0 = rocheHeroTeaserCss;

const RocheHeroTeaser$1 = /*@__PURE__*/ proxyCustomElement(class RocheHeroTeaser extends HTMLElement {
    handleResize() {
        this.isMobile = window.innerWidth <= breakpoints.small;
        this.handleSlottedImage();
    }
    get slottedButton() {
        return this.element.querySelector('roche-button');
    }
    get slottedImage() {
        return this.element.querySelector('roche-image');
    }
    get defaultImage() {
        return this.element.shadowRoot.querySelector('.teaser-image');
    }
    async componentWillLoad() {
        this.translations = {
            readMore: await TranslationService.translate('read more'),
        };
        this.handleResize();
    }
    componentDidLoad() {
        this.handleSlottedButtons();
        this.handleSlottedImage();
        this.handleResize();
        this.imageRatioIdentifier();
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.teaserTitle = undefined;
        this.teaserOverline = undefined;
        this.teaserDescription = undefined;
        this.teaserImage = undefined;
        this.teaserHeroImage = undefined;
        this.teaserUrl = undefined;
        this.teaserSecondaryAction = undefined;
        this.teaserImageRatio = Ratio.SQUARE;
        this.mirrorLayout = false;
        this.buttonTitle = undefined;
        this.layout = Layout.DEFAULT;
        this.leftIcon = undefined;
        this.rightIcon = undefined;
        this.isMobile = false;
        this.translations = undefined;
        this.handleResize = debounce_1.debounce(this.handleResize.bind(this), 50);
        this.trackButtonClick = this.trackButtonClick.bind(this);
    }
    render() {
        const layoutWithoutOverline = this.layout === Layout.DEFAULT_WITHOUT_OVERLINE;
        const teaserClasses = {
            teaser: true,
            'teaser--image-on-left': this.mirrorLayout,
        };
        return (h(Host, { key: 'c08c1297584a8727c93bfdb5d53c021f36d7dd10' }, h("div", { key: '0924b263a979270d2b06588dfa4a4e3ff4cfe99b', class: teaserClasses }, h("div", { key: '6403b77fb30fe3ae2fb77998554c14714853b584', class: "teaser__text-wrapper" }, this.teaserOverline && !layoutWithoutOverline
            && h("div", { class: "teaser__overline" }, this.teaserOverline), this.teaserTitle
            && h("div", { class: "teaser__title" }, this.teaserTitle), this.teaserDescription
            && h("p", { class: "teaser__description" }, this.teaserDescription), h("div", { key: 'ee2b7ba523e96f55c0ad34cd69baa5de877dd7df', class: "teaser__buttons-wrapper" }, h("roche-button", { key: '116fd28a2eb83d751ae18738fbc09dd8fabae2c6', href: this.teaserUrl, appearance: ButtonAppearance.SECONDARY, text: this.buttonTitle ? this.buttonTitle
                : StringService.capitalize(this.translations.readMore), onClick: this.trackButtonClick, leftIcon: this.leftIcon, rightIcon: this.rightIcon }), this.teaserSecondaryAction
            && h("slot", { name: "secondary-action" }))), h("div", { key: 'fefba2a8d62458df3d0952a7255546751af118cd', class: "teaser__image-wrapper" }, !this.teaserHeroImage && this.teaserImage
            && h("roche-image", { class: "teaser-image", src: this.teaserImage, ratio: this.teaserImageRatio }), this.teaserHeroImage
            && h("slot", { name: "hero-image" })))));
    }
    handleSlottedButtons() {
        var _a;
        (_a = this.slottedButton) === null || _a === void 0 ? void 0 : _a.setAttribute('appearance', ButtonAppearance.TERTIARY);
    }
    imageRatioIdentifier() {
        var _a, _b;
        if (((_a = this.slottedImage) === null || _a === void 0 ? void 0 : _a.ratio) === Ratio.SQUARE || ((_b = this.defaultImage) === null || _b === void 0 ? void 0 : _b.ratio) === Ratio.SQUARE) {
            this.element.setAttribute('square-image', 'true');
        }
    }
    handleSlottedImage() {
        var _a, _b, _c, _d, _e, _f;
        const imageIsSquare = this.element.hasAttribute('square-image');
        const imageIsHD = ((_a = this.slottedImage) === null || _a === void 0 ? void 0 : _a.ratio) === Ratio.HD
            || ((_b = this.defaultImage) === null || _b === void 0 ? void 0 : _b.ratio) === Ratio.HD;
        if (!imageIsSquare) {
            return;
        }
        if (this.isMobile && imageIsSquare && !imageIsHD) {
            (_c = this.slottedImage) === null || _c === void 0 ? void 0 : _c.setAttribute('ratio', Ratio.HD);
            (_d = this.defaultImage) === null || _d === void 0 ? void 0 : _d.setAttribute('ratio', Ratio.HD);
        }
        if (!this.isMobile && imageIsSquare && imageIsHD) {
            (_e = this.slottedImage) === null || _e === void 0 ? void 0 : _e.setAttribute('ratio', Ratio.SQUARE);
            (_f = this.defaultImage) === null || _f === void 0 ? void 0 : _f.setAttribute('ratio', Ratio.SQUARE);
        }
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            event: 'teaser_click',
            component_name: 'roche-hero-teaser',
        };
    }
    trackButtonClick() {
        TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { additionalParameters: {
                teaser_title: this.teaserTitle,
                teaser_url: this.teaserUrl,
            } }));
    }
    get element() { return this; }
    static get style() { return RocheHeroTeaserStyle0; }
}, [1, "roche-hero-teaser", {
        "teaserTitle": [1, "teaser-title"],
        "teaserOverline": [1, "teaser-overline"],
        "teaserDescription": [1, "teaser-description"],
        "teaserImage": [1, "teaser-image"],
        "teaserHeroImage": [1, "teaser-hero-image"],
        "teaserUrl": [1, "teaser-url"],
        "teaserSecondaryAction": [1, "teaser-secondary-action"],
        "teaserImageRatio": [1, "teaser-image-ratio"],
        "mirrorLayout": [4, "mirror-layout"],
        "buttonTitle": [1, "button-title"],
        "layout": [1],
        "leftIcon": [1, "left-icon"],
        "rightIcon": [1, "right-icon"],
        "isMobile": [32],
        "translations": [32]
    }, [[9, "resize", "handleResize"]]]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-hero-teaser", "roche-button", "roche-icon", "roche-image", "roche-video-player"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-hero-teaser":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheHeroTeaser$1);
            }
            break;
        case "roche-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-image":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-video-player":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheHeroTeaser = RocheHeroTeaser$1;
const defineCustomElement = defineCustomElement$1;

export { RocheHeroTeaser, defineCustomElement };

//# sourceMappingURL=roche-hero-teaser.js.map